import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Book,
  Home as HomeIcon,
  Circle,
  DollarSign,
  Compass,
} from "lucide-react";
import Logo from "../../assets/TheBC.png";
import { signOut } from "@aws-amplify/auth";
import { clearAuthType } from "../../auth/authTypeStorage";
import { requestReaderGreeting } from "../../services/readerActivity";
import { resolveSessionForType } from "../../auth/resolveSession";

import MyCircles from "./MyCircles";
import RHome from "./RHome";
import MyTBR from "./MyTBR";
import MyOrders from "./MyOrders";
import ReaderQuests from "./ReaderQuests";


/** —— TABS ——————————————————————————————————— */
const validTabs = [
  "home",
  "mytbr",
  "mycircles",
  "myorders",
  "quests",
];

function normalizeTabParam(val) {
  if (!val) return "";
  const key = String(val).toLowerCase();
  return validTabs.includes(key) ? key : "";
}


const RMainPage = () => {
  const [firstName, setFirstName] = useState("Reader");
  const [profileError, setProfileError] = useState(false);
  const [profileRetry, setProfileRetry] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const [displayName, setDisplayName] = useState("");


  const location = useLocation();
  const navigate = useNavigate();

  /** Initial tab */
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = normalizeTabParam(params.get("tab"));
    if (fromQuery) return fromQuery;

    const stateTab = location.state?.activeTab;
    return validTabs.includes(stateTab) ? stateTab : "home";
  });



  /** Load user name */
  useEffect(() => {
    let version = 0, controller;
    const loadUserName = async () => {
      const request = ++version;
      controller?.abort(); controller = new AbortController();
      setFirstName("Reader"); setDisplayName(""); setProfileError(false);
      try {
        const data = await requestReaderGreeting({ signal: controller.signal });
        if (request === version) { setFirstName(data.firstName || "Reader"); setDisplayName(data.displayName); }
      } catch { if (request === version) setProfileError(true); }
    };
    const refresh = () => { if (!document.hidden) loadUserName(); };
    loadUserName();
    window.addEventListener('bookish:reader-profile-updated', loadUserName);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { ++version; controller?.abort(); window.removeEventListener('bookish:reader-profile-updated', loadUserName); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [profileRetry]);


  /** Sync tab with URL (and Stripe redirects) */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const payment = params.get("payment");
    const connect = params.get("connect");
    const isReturn =
      payment === "success" ||
      payment === "cancelled" ||
      connect === "done" ||
      connect === "canceled";

    const incomingTab = normalizeTabParam(params.get("tab"));

    let nextTab = "home";

    if (incomingTab) nextTab = incomingTab;
    else if (isReturn) nextTab = "myorders";


    if (nextTab !== activeTab) setActiveTab(nextTab);

    const canonical = new URLSearchParams(location.search);
    canonical.set("tab", nextTab);
    canonical.delete("payment");
    canonical.delete("connect");

    if (canonical.toString() !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: canonical.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  /** Helpers */
  const setTabAndUrl = (next) => {
    let normalized = normalizeTabParam(next) || "home";

    setActiveTab(normalized);
    const params = new URLSearchParams(location.search);
    params.set("tab", normalized);
    params.delete("payment");
    params.delete("connect");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const handleTabChange = (tab) => { if (window.dispatchEvent(new Event("bookish:before-navigation", { cancelable: true }))) setTabAndUrl(tab); };

  const handleSignOut = async () => {
    if (signingOut || !window.dispatchEvent(new Event("bookish:before-navigation", { cancelable: true }))) return;
    setSigningOut(true); setSignOutError("");
    try {
      await resolveSessionForType("reader", { restore: true, read: () => signOut() });
      localStorage.removeItem("user");
      clearAuthType();
      navigate("/", { replace: true });
    } catch (error) {
      setSignOutError("We couldn’t sign you out. Please try again.");
      setSigningOut(false);
    }
  };


  /** Render main content */
  const renderMainContent = () => {
  switch (activeTab) {
    case "home":
      return <RHome />;
    case "mytbr":
      return <MyTBR />;
    case "mycircles":
      return <MyCircles />;
    case "myorders":
      return <MyOrders/>;
    case "quests":
      return <ReaderQuests/>;
    default:
      return <RHome />;
  }
};
  /**
   * NAV sections
   */
  const navSections = [
  {
    items: [
      {
        key: "home",
        label: "Home",
        icon: HomeIcon,
        onClick: () => handleTabChange("home"),
        active: activeTab === "home",
      },
      {
        key: "mytbr",
        label: "Bookshelf",
        icon: Book,
        onClick: () => handleTabChange("mytbr"),
        active: activeTab === "mytbr",
      },
      {
        key: "mycircles",
        label: "My Circles",
        icon: Circle,
        onClick: () => handleTabChange("mycircles"),
        active: activeTab === "mycircles",
      },
      {
        key: "myorders",
        label: "Orders",
        icon: DollarSign,
        onClick: () => handleTabChange("myorders"),
        active: activeTab === "myorders",
      },
      {
        key: "quests",
        label: "Quests",
        icon: Compass,
        onClick: () => handleTabChange("quests"),
        active: activeTab === "quests",
      },
    ],
  },
];

  return (
  <div className="min-h-screen flex flex-col bg-bookish-white text-bookish-purple">
    
    {/* TOP BAR */}
    <header className="w-full bg-bookish-royal text-bookish-cream">
      <div className="w-full px-4 md:px-6 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex flex-col">
            <h1 className="text-xl font-serif font-bold break-words">
              Welcome back, {firstName}
            </h1>

            {profileError && <p role="status" className="text-sm">Your greeting is unavailable. <button className="underline min-h-[44px]" onClick={() => setProfileRetry(value => value + 1)}>Retry greeting</button></p>}
            {displayName && (
              <span className="text-xs tracking-widest uppercase text-bookish-cream/80">
                {displayName}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 md:gap-6 text-sm font-semibold">
            <NavLink
              to="/market"
              className="hover:text-bookish-cream transition"
            >
              Bookish Circle Market
            </NavLink>

            <button
              disabled={signingOut}
              onClick={handleSignOut}
              className="min-h-[44px] text-bookish-cream hover:underline transition disabled:opacity-60"
            >
              {signingOut ? "Signing out…" : "Sign Out"}
            </button>
          </div>
        </div>
      </div>
    </header>

    {signOutError && <p role="alert" className="p-4 text-red-700">{signOutError}</p>}
    {/* MAIN BODY */}
    <div className="flex flex-col md:flex-row flex-1 min-w-0">

      {/* Named mobile tabs leave the full screen width for the reading journal. */}
      <nav aria-label="Reader Portal mobile navigation" className="md:hidden grid grid-cols-5 w-full bg-gradient-to-b from-[#F3E7D2] via-[#F1E1C7] to-[#EAD6B6] border-b border-bookish-purple/15 p-2 gap-1">
        {navSections[0].items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={item.onClick}
              aria-label={item.label}
              aria-current={item.active ? "page" : undefined}
              title={item.label}
              className={`flex flex-col gap-1 items-center justify-center min-h-[60px] px-1 py-2 rounded-lg transition ${
                item.active
                  ? "bg-bookish-white border-b-4 border-bookish-purple text-bookish-purple"
                  : "text-bookish-purpleAccent hover:bg-bookish-cream/60"
              }`}
            >
              <Icon aria-hidden="true" className="h-5 w-5" />
              <span className="text-xs leading-tight text-center">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* SIDEBAR */}
      <aside
        className="
          hidden md:flex md:w-[250px] lg:w-[300px]
          flex-col
          relative overflow-hidden
          bg-gradient-to-b
          from-[#F3E7D2] via-[#F1E1C7] to-[#EAD6B6]
          border-r border-bookish-purple/15
        "
      >
        {/* subtle parchment noise */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-multiply"
          style={{
            backgroundImage:
              "radial-gradient(rgba(63,37,107,0.10) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />

        <div className="relative z-10 flex flex-col flex-1">

          {/* Logo */}
          <div className="flex justify-center pt-6">
            <img
              src={Logo}
              alt="Bookish Circle Logo"
              className="h-[100px] sm:h-[120px] md:h-[140px] w-auto"
            />
          </div>

          {/* Portal Title */}
          <div className="pb-4">
            <h2 className="text-lg font-serif text-center font-semibold text-bookish-purple tracking-[0.05em]">
              Reader Portal
            </h2>
          </div>

          {/* NAV */}
          <nav aria-label="Reader Portal navigation" className="px-4 space-y-1">
            {navSections[0].items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={item.onClick}
                  className={navStyle(item.active)}
                  aria-current={item.active ? "page" : undefined}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Spacer to push bottom if needed later */}
          <div className="flex-1" />
        </div>
      </aside>

      {/* CONTENT AREA */}
      <main className="flex-1 min-w-0 p-4 md:p-8 bg-[#F6EEDD]">
        {renderMainContent()}
      </main>
    </div>
  </div>
);


  
};


const navStyle = (isActive, isBold = false) =>
  `w-full flex items-center px-3 py-1.5 min-h-[44px] text-[18px] leading-[1.35] transition ${
    isActive
      ? "bg-bookish-white border-l-4 border-bookish-purple text-bookish-purple"
      : "text-bookish-purpleAccent hover:border-l-4 hover:border-bookish-cream hover:bg-bookish-royal hover:text-bookish-cream"
  } ${isBold ? "font-semibold" : "font-medium"}`;

export default RMainPage;
